import { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import { Flex, Spinner } from '@radix-ui/themes'
import { IconPencil } from '@tabler/icons-react'
import { useUpdateQuestion } from '@/services/updateQuestionService'
import { QuestionsItemType } from '@/types/module'
import {
  UpdateQuestionBodyType,
  UpdateSubQuestion,
} from '@/types/question.type'
import { toast } from 'sonner'
import { FormatFullDateMonthYear } from '@/utils/time'
import { Button } from '@/components/ui/button'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Textarea } from '@/components/ui/textarea'
import AnserItem from './AnserItem'
import QuestionItemContainer from './QuestionItemContainer'
import QuestionTypeItem from './QuestionTypeItem'

const ChoicesQuestion = ({
  questionData,
  refreshQuestionList,
}: {
  questionData: QuestionsItemType
  refreshQuestionList: () => void
}) => {
  const { updateQuestionAsync, isUpdating } = useUpdateQuestion()

  const [isEditing, setIsEditing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [editedQuestion, setEditedQuestion] = useState(questionData.text)
  const [editedOptions, setEditedOptions] = useState(
    questionData.sub_questions[0].options
  )
  const [editedScore, setEditedScore] = useState(
    questionData.sub_questions[0].marks
  )
  const [editedTime, setEditedTime] = useState(
    questionData.sub_questions[0].time_limit
  )

  // Validation state
  const isQuestionValid = editedQuestion.trim().length > 0
  const isAllOptionsValid = editedOptions.every(
    (option) => option.text.trim().length > 0
  )
  const isAtLeastOneCorrect = editedOptions.some((option) => option.is_correct)
  const isScoreValid = Number.isFinite(editedScore) && editedScore >= 1
  const isTimeValid = Number.isFinite(editedTime) && editedTime >= 1
  const canSave =
    isQuestionValid &&
    isAllOptionsValid &&
    isAtLeastOneCorrect &&
    isScoreValid &&
    isTimeValid

  const handleQuestionChange = (newText: string) => {
    setEditedQuestion(newText)
  }

  const handleOptionChange = (index: number, newOption: string) => {
    const updatedOptions = [...editedOptions]
    updatedOptions[index] = {
      ...updatedOptions[index],
      text: newOption,
    }
    setEditedOptions(updatedOptions)
  }

  const handleCheckboxChange = (index: number, isCorrect: boolean) => {
    const updatedOptions = [...editedOptions]
    updatedOptions[index] = {
      ...updatedOptions[index],
      is_correct: isCorrect,
    }
    setEditedOptions(updatedOptions)
  }

  const handleSave = () => {
    const newData = {
      ...questionData,
      text: editedQuestion.trim(),
      sub_questions: [
        {
          ...questionData.sub_questions[0],
          options: editedOptions.map((option) => ({
            ...option,
            text: option.text.trim(),
          })),
        },
      ],
    }
    console.log('New question data:', newData)

    const subQuestionsPayload: UpdateSubQuestion[] = [
      {
        id: questionData.sub_questions[0].sub_question_id,
        mark: editedScore,
        text: questionData.sub_questions[0].text,
        time_required: editedTime,
        options: editedOptions.map((option) => ({
          id: option.option_id,
          option_text: option.text.trim(),
          is_correct: option.is_correct,
        })),
        update_marking_scheme: [],
        indicative_content: '',
      },
    ]

    const reqBody: UpdateQuestionBodyType = {
      text: editedQuestion.trim(),

      sub_questions: subQuestionsPayload,
    }

    updateQuestionAsync({
      questionId: String(questionData.question_id),
      data: reqBody,
    })
      .then(() => {
        refreshQuestionList()
        setIsEditing(false)
        toast.success('Update question successfully', {
          position: 'top-right',
          duration: 3000,
        })
      })
      .catch((e: AxiosError) => {
        // Fix: Provide a fallback error message if detail is undefined
        const detail =
          (e.response?.data as { detail?: string })?.detail ||
          'An error occurred while saving the question.'
        toast.error(detail)
        console.log(e)
      })
  }

  const handleCancel = () => {
    setEditedQuestion(questionData.text)
    setEditedOptions(questionData.sub_questions[0].options)
    setEditedScore(questionData.sub_questions[0].marks)
    setEditedTime(questionData.sub_questions[0].time_limit)
    setIsEditing(false)
  }

  // Ensure collapsible expands whenever editing starts
  useEffect(() => {
    if (isEditing) setIsOpen(true)
  }, [isEditing])

  // Totals across all sub-questions (use edited values for the first one)
  const totalScore = questionData.sub_questions.reduce((sum, sq, index) => {
    return sum + (index === 0 ? editedScore : sq.marks)
  }, 0)
  const totalTime = questionData.sub_questions.reduce((sum, sq, index) => {
    return sum + (index === 0 ? editedTime : sq.time_limit)
  }, 0)
  return (
    <QuestionItemContainer
      time={totalTime}
      score={totalScore}
      isEditing={isEditing}
      editableScore={{
        value: editedScore,
        onChange: (v) => setEditedScore(v),
        invalid: !isScoreValid,
      }}
      editableTime={{
        value: editedTime,
        onChange: (v) => setEditedTime(v),
        invalid: !isTimeValid,
      }}
    >
      <Collapsible
        className='w-full text-[#002652]'
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <CollapsibleTrigger className='w-full'>
          <Flex
            justify={'between'}
            align={'center'}
            className='w-full font-light text-[#475467]'
          >
            <p className='font-light text-[#475467]'>
              {questionData.question_info}
            </p>
            <Flex gap={'12px'} align={'center'} className='text-sm'>
              {!questionData.in_pt && (
                <Flex justify={'end'} align={'center'} className='h-11'>
                  {!isEditing && (
                    <Button
                      className='mb-2'
                      onClick={() => {
                        setIsEditing(true)
                        setIsOpen(true)
                      }}
                    >
                      {isUpdating ? (
                        <Spinner />
                      ) : (
                        <>
                          <IconPencil /> Edit
                        </>
                      )}
                    </Button>
                  )}
                  {isEditing && (
                    <Flex className='mb-2' gap={'2'}>
                      <Button variant={'outline'} onClick={handleCancel}>
                        Cancel
                      </Button>
                      <Button
                        onClick={handleSave}
                        disabled={!canSave || isUpdating}
                        className={
                          !canSave ? 'cursor-not-allowed opacity-50' : ''
                        }
                      >
                        Save
                      </Button>
                    </Flex>
                  )}
                </Flex>
              )}
              <p className='font-light'>
                Date created: {FormatFullDateMonthYear(questionData.created_at)}
              </p>
              <QuestionTypeItem title={`Section ${questionData.section}`} />
              <QuestionTypeItem title='Multiple Choice' />
            </Flex>
          </Flex>
          {!isEditing && (
            <p className='mt-2 text-left text-[#002652]'>{questionData.text}</p>
          )}
        </CollapsibleTrigger>
        {isEditing && (
          <div className='mt-2'>
            <Textarea
              value={editedQuestion}
              onChange={(e) => handleQuestionChange(e.target.value)}
              className={`min-h-[80px] text-left text-[#002652] ${
                !isQuestionValid && editedQuestion.length > 0
                  ? 'border-red-500 focus:border-red-500'
                  : ''
              }`}
              placeholder='Enter question text...'
            />
            {!isQuestionValid && editedQuestion.length > 0 && (
              <p className='mt-1 text-sm text-red-500'>
                Question text cannot be blank
              </p>
            )}
          </div>
        )}
        <CollapsibleContent className='mt-2'>
          <Flex direction={'column'} gap={'12px'} className='px-5'>
            {editedOptions.map((item, index) => (
              <AnserItem
                key={item.option_id}
                checked={item.is_correct}
                answer={item.text}
                isEditing={isEditing}
                onAnswerChange={(newAnswer) =>
                  handleOptionChange(index, newAnswer)
                }
                onCheckedChange={(isCorrect) =>
                  handleCheckboxChange(index, isCorrect)
                }
              />
            ))}
            {isEditing && !isAtLeastOneCorrect && (
              <p className='mt-1 px-1 text-sm text-red-500'>
                At least one option must be marked as correct
              </p>
            )}
          </Flex>
        </CollapsibleContent>
      </Collapsible>
    </QuestionItemContainer>
  )
}

export default ChoicesQuestion
